import React, { useEffect, useMemo, useState } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { subscribeLive } from '../../utils/liveData';
import { useAuth } from '../../hooks/useAuth';
import { normalizeSearchText } from '../../utils';
import ModernSelect from '../common/ModernSelect';
import './Todos.css';

/*
 * Yapılacaklar: ekip içi görev listesi.
 * Kişiler ('taskPeople') yalnızca isimdir, uygulama hesabına bağlı değildir; görevler ('tasks') bu kişilere atanır.
 * Herkes tüm görevleri görür ve "Yapıldı" olarak işaretleyebilir.
 */

const PRIORITIES = [
  { value: 'high', label: 'Yüksek' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Düşük' }
];
const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };
const PERSON_COLORS = 8; // Todos.css'teki .todo-avatar[data-color] sayısı

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// "YYYY-MM-DD" → bugüne göre gün farkı (geçmiş negatif)
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(y, m - 1, d) - today) / 86400000);
};

const dueLabel = (dateStr) => {
  const diff = daysUntil(dateStr);
  if (diff === null) return null;
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Yarın';
  if (diff < 0) return `${-diff} gün gecikti`;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w.charAt(0).toLocaleUpperCase('tr-TR')).join('') || '?';

const emptyDraft = () => ({ title: '', description: '', assigneeId: '', priority: 'normal', dueDate: '' });

const PersonAvatar = ({ person, name, size = 'md' }) => (
  <span className={`todo-avatar is-${size}`} data-color={person ? person.color % PERSON_COLORS : 'none'} title={name || 'Atanmamış'}>
    {name ? initials(name) : <i className="fas fa-user"></i>}
  </span>
);

const Todos = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [draft, setDraft] = useState(emptyDraft);
  const [showDraftNote, setShowDraftNote] = useState(false);
  const [saving, setSaving] = useState(false);

  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'done' | 'all'
  const [personFilter, setPersonFilter] = useState('all'); // 'all' | 'none' | kişi id
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // düzenlenen görevin taslağı (id dahil)
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [peopleOpen, setPeopleOpen] = useState(false);

  // Canlı veri: ekipten biri görev ekleyince / işaretleyince liste anında güncellenir
  useEffect(() => {
    const onErr = (err) => {
      console.error('Yapılacaklar yüklenemedi:', err);
      setError('Yapılacaklar yüklenemedi. Sayfayı yenileyip tekrar deneyin.');
      setLoading(false);
    };
    const unsubTasks = subscribeLive('tasks', collection(db, 'tasks'), (snap) => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, onErr);
    const unsubPeople = subscribeLive('taskPeople', collection(db, 'taskPeople'), (snap) => {
      setPeople(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')));
    }, onErr);
    return () => { unsubTasks(); unsubPeople(); };
  }, []);

  const personById = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);
  // Kişi silinmişse görevdeki ad gösterilir
  const assigneeName = (task) => personById.get(task.assigneeId)?.name || task.assigneeName || '';

  const counts = useMemo(() => {
    const open = tasks.filter(t => !t.done);
    return {
      open: open.length,
      done: tasks.length - open.length,
      today: open.filter(t => daysUntil(t.dueDate) === 0).length,
      overdue: open.filter(t => (daysUntil(t.dueDate) ?? 0) < 0).length
    };
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    const term = normalizeSearchText(search);
    return tasks
      .filter((t) => {
        if (statusFilter === 'open' && t.done) return false;
        if (statusFilter === 'done' && !t.done) return false;
        if (personFilter === 'none' && t.assigneeId) return false;
        if (personFilter !== 'all' && personFilter !== 'none' && t.assigneeId !== personFilter) return false;
        if (term && !normalizeSearchText(`${t.title} ${t.description || ''} ${assigneeName(t)}`).includes(term)) return false;
        return true;
      })
      .sort((a, b) => {
        // Açıklar önce; açıklarda son tarih (yakın önce, tarihsizler sonda) → öncelik → eklenme
        if (a.done !== b.done) return a.done ? 1 : -1;
        if (a.done) return (b.doneAt || '').localeCompare(a.doneAt || '');
        const da = a.dueDate || '9999-12-31';
        const dbb = b.dueDate || '9999-12-31';
        if (da !== dbb) return da.localeCompare(dbb);
        const pr = (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1);
        if (pr !== 0) return pr;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, statusFilter, personFilter, search, personById]);

  const personOptions = [{ value: '', label: 'Atanmamış' }, ...people.map(p => ({ value: p.id, label: p.name }))];

  const taskPayload = (d) => {
    const person = personById.get(d.assigneeId);
    return {
      title: d.title.trim(),
      description: (d.description || '').trim(),
      assigneeId: person ? person.id : '',
      assigneeName: person ? person.name : '',
      priority: d.priority || 'normal',
      dueDate: d.dueDate || '',
      updatedAt: new Date().toISOString()
    };
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, 'tasks'), {
        ...taskPayload(draft),
        done: false,
        doneAt: '',
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || '',
        createdByName: user?.displayName || user?.email?.split('@')[0] || ''
      });
      // Kişi ve öncelik bir sonraki görev için korunur (art arda aynı kişiye görev girerken pratik)
      setDraft(prev => ({ ...emptyDraft(), assigneeId: prev.assigneeId, priority: prev.priority }));
      setShowDraftNote(false);
    } catch (err) {
      console.error('Görev eklenemedi:', err);
      alert('Görev eklenemedi. Yetkinizi kontrol edip tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = (task) => {
    const done = !task.done;
    updateDoc(doc(db, 'tasks', task.id), {
      done,
      doneAt: done ? new Date().toISOString() : '',
      doneByName: done ? (user?.displayName || user?.email?.split('@')[0] || '') : '',
      updatedAt: new Date().toISOString()
    }).catch((err) => {
      console.error('Görev güncellenemedi:', err);
      alert('Görev güncellenemedi.');
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing?.title.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'tasks', editing.id), taskPayload(editing));
      setEditing(null);
    } catch (err) {
      console.error('Görev kaydedilemedi:', err);
      alert('Görev kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'tasks', confirmDelete.id));
      setConfirmDelete(null);
      setEditing(null);
    } catch (err) {
      console.error('Görev silinemedi:', err);
      alert('Görev silinemedi.');
    }
  };

  return (
    <div className="todo-page">
      <header className="todo-header">
        <div>
          <h2><i className="fas fa-clipboard-check"></i> Yapılacaklar</h2>
          <p>Ekip içi görevler: kişilere atayın, bitince işaretleyin.</p>
        </div>
        <button type="button" className="todo-btn-ghost" onClick={() => setPeopleOpen(true)}>
          <i className="fas fa-user-friends"></i> Kişiler <span className="todo-badge">{people.length}</span>
        </button>
      </header>

      <div className="todo-stats">
        <button type="button" className="todo-stat is-open" onClick={() => setStatusFilter('open')}>
          <span>Açık görev</span><strong>{counts.open}</strong>
        </button>
        <div className="todo-stat is-today"><span>Bugün teslim</span><strong>{counts.today}</strong></div>
        <div className={`todo-stat is-overdue${counts.overdue ? ' has-items' : ''}`}><span>Geciken</span><strong>{counts.overdue}</strong></div>
        <button type="button" className="todo-stat is-done" onClick={() => setStatusFilter('done')}>
          <span>Tamamlanan</span><strong>{counts.done}</strong>
        </button>
      </div>

      {/* Hızlı ekleme */}
      <form className="todo-add" onSubmit={handleAdd}>
        <div className="todo-add-main">
          <i className="fas fa-plus todo-add-icon" aria-hidden="true"></i>
          <input
            type="text"
            className="todo-add-title"
            placeholder="Yeni görev yazın… (örn. Kadıköy montajı için malzeme hazırla)"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            aria-label="Görev başlığı"
          />
        </div>
        {showDraftNote && (
          <textarea
            className="todo-add-note"
            rows="2"
            placeholder="Açıklama (isteğe bağlı)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        )}
        <div className="todo-add-meta">
          <ModernSelect
            className="todo-select"
            ariaLabel="Atanan kişi"
            value={draft.assigneeId}
            onChange={(v) => setDraft({ ...draft, assigneeId: v })}
            options={personOptions}
          />
          <ModernSelect
            className="todo-select"
            ariaLabel="Öncelik"
            value={draft.priority}
            onChange={(v) => setDraft({ ...draft, priority: v })}
            options={PRIORITIES.map(p => ({ ...p, label: `${p.label} öncelik` }))}
          />
          <input
            type="date"
            className="todo-date"
            value={draft.dueDate}
            min={todayStr()}
            onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
            aria-label="Son tarih"
          />
          <button type="button" className="todo-btn-link" onClick={() => setShowDraftNote(v => !v)}>
            <i className={`fas fa-${showDraftNote ? 'minus' : 'align-left'}`}></i> Açıklama
          </button>
          <button type="submit" className="todo-btn-primary" disabled={!draft.title.trim() || saving}>
            {saving ? 'Ekleniyor…' : 'Görev Ekle'}
          </button>
        </div>
        {people.length === 0 && (
          <p className="todo-hint">
            Görev atamak için önce <button type="button" onClick={() => setPeopleOpen(true)}>kişi ekleyin</button>.
          </p>
        )}
      </form>

      {/* Filtreler */}
      <div className="todo-toolbar">
        <div className="todo-segment" role="tablist" aria-label="Durum">
          {[['open', 'Açık', counts.open], ['done', 'Yapıldı', counts.done], ['all', 'Tümü', tasks.length]].map(([value, label, n]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={statusFilter === value}
              className={statusFilter === value ? 'is-active' : ''}
              onClick={() => setStatusFilter(value)}
            >
              {label} <span>{n}</span>
            </button>
          ))}
        </div>
        <div className="todo-search">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Görev ara…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <ModernSelect
          className="todo-select"
          ariaLabel="Kişiye göre"
          value={personFilter}
          onChange={setPersonFilter}
          options={[{ value: 'all', label: 'Tüm kişiler' }, { value: 'none', label: 'Atanmamış' }, ...people.map(p => ({ value: p.id, label: p.name }))]}
        />
      </div>

      {/* Liste */}
      {loading ? (
        <div className="todo-empty"><div className="todo-spinner"></div><p>Görevler yükleniyor…</p></div>
      ) : error ? (
        <div className="todo-empty is-error"><p>{error}</p></div>
      ) : visibleTasks.length === 0 ? (
        <div className="todo-empty">
          <i className="fas fa-clipboard-list"></i>
          <p>{statusFilter === 'open' && tasks.length > 0 ? 'Açık görev yok, her şey tamam! 🎉' : 'Bu filtreye uygun görev yok.'}</p>
        </div>
      ) : (
        <ul className="todo-list">
          {visibleTasks.map((task) => {
            const name = assigneeName(task);
            const diff = daysUntil(task.dueDate);
            const dueTone = task.done ? '' : diff === null ? '' : diff < 0 ? 'is-late' : diff === 0 ? 'is-today' : diff === 1 ? 'is-soon' : '';
            return (
              <li key={task.id} className={`todo-item${task.done ? ' is-done' : ''}`} data-priority={task.priority || 'normal'}>
                <button
                  type="button"
                  className="todo-check"
                  onClick={() => toggleDone(task)}
                  aria-pressed={!!task.done}
                  aria-label={task.done ? 'Yapılmadı olarak işaretle' : 'Yapıldı olarak işaretle'}
                  title={task.done ? 'Geri al' : 'Yapıldı'}
                >
                  <i className="fas fa-check"></i>
                </button>

                <button type="button" className="todo-body" onClick={() => setEditing({ ...emptyDraft(), ...task })}>
                  <span className="todo-title">{task.title}</span>
                  {task.description && <span className="todo-desc">{task.description}</span>}
                  <span className="todo-meta">
                    {task.priority === 'high' && <span className="todo-pill is-high"><i className="fas fa-arrow-up"></i> Yüksek</span>}
                    {task.priority === 'low' && <span className="todo-pill is-low">Düşük</span>}
                    {task.dueDate && (
                      <span className={`todo-pill is-date ${dueTone}`}><i className="far fa-calendar"></i> {dueLabel(task.dueDate)}</span>
                    )}
                    {task.done && task.doneAt && (
                      <span className="todo-pill is-donemark">
                        <i className="fas fa-check-circle"></i> {new Date(task.doneAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                        {task.doneByName ? ` · ${task.doneByName}` : ''}
                      </span>
                    )}
                  </span>
                </button>

                <span className="todo-assignee">
                  <PersonAvatar person={personById.get(task.assigneeId)} name={name} />
                  <span className="todo-assignee-name">{name || 'Atanmamış'}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Görev düzenle */}
      {editing && (
        <div className="todo-modal-overlay" onClick={() => setEditing(null)}>
          <form className="todo-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSaveEdit}>
            <div className="todo-modal-head">
              <h3>Görevi Düzenle</h3>
              <button type="button" className="todo-icon-btn" onClick={() => setEditing(null)} aria-label="Kapat"><i className="fas fa-times"></i></button>
            </div>
            <label className="todo-field">
              <span>Görev</span>
              <input type="text" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} autoFocus />
            </label>
            <label className="todo-field">
              <span>Açıklama</span>
              <textarea rows="3" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </label>
            <div className="todo-field-row">
              <div className="todo-field">
                <span>Atanan kişi</span>
                <ModernSelect fullWidth value={editing.assigneeId || ''} onChange={(v) => setEditing({ ...editing, assigneeId: v })} options={personOptions} ariaLabel="Atanan kişi" />
              </div>
              <div className="todo-field">
                <span>Öncelik</span>
                <ModernSelect fullWidth value={editing.priority || 'normal'} onChange={(v) => setEditing({ ...editing, priority: v })} options={PRIORITIES} ariaLabel="Öncelik" />
              </div>
              <label className="todo-field">
                <span>Son tarih</span>
                <input type="date" value={editing.dueDate || ''} onChange={(e) => setEditing({ ...editing, dueDate: e.target.value })} />
              </label>
            </div>
            {editing.createdByName && (
              <p className="todo-modal-info">
                {editing.createdByName} ekledi · {new Date(editing.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
              </p>
            )}
            <div className="todo-modal-actions">
              <button type="button" className="todo-btn-danger-ghost" onClick={() => setConfirmDelete(editing)}>
                <i className="fas fa-trash-alt"></i> Sil
              </button>
              <span className="todo-spacer"></span>
              <button type="button" className="todo-btn-ghost" onClick={() => setEditing(null)}>Vazgeç</button>
              <button type="submit" className="todo-btn-primary" disabled={!editing.title.trim() || saving}>Kaydet</button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="todo-modal-overlay is-top" onClick={() => setConfirmDelete(null)}>
          <div className="todo-modal is-small" onClick={(e) => e.stopPropagation()}>
            <h3>Görev silinsin mi?</h3>
            <p className="todo-modal-text">"{confirmDelete.title}" kalıcı olarak silinecek.</p>
            <div className="todo-modal-actions">
              <span className="todo-spacer"></span>
              <button type="button" className="todo-btn-ghost" onClick={() => setConfirmDelete(null)}>Vazgeç</button>
              <button type="button" className="todo-btn-danger" onClick={handleDelete}>Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {peopleOpen && <PeopleManager people={people} tasks={tasks} onClose={() => setPeopleOpen(false)} />}
    </div>
  );
};

// Kişiler: ekle / yeniden adlandır / sil. Silinen kişinin görevleri adıyla kalır.
const PeopleManager = ({ people, tasks, onClose }) => {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null); // { id, name }
  const [confirmId, setConfirmId] = useState(null);

  const openCount = (id) => tasks.filter(t => t.assigneeId === id && !t.done).length;

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (people.some(p => p.name.toLocaleLowerCase('tr-TR') === trimmed.toLocaleLowerCase('tr-TR'))) {
      alert('Bu isimde bir kişi zaten var.');
      return;
    }
    // Renk sırayla dağıtılır: en az kullanılan renk
    const used = Array.from({ length: PERSON_COLORS }, (_, i) => people.filter(p => p.color === i).length);
    const color = used.indexOf(Math.min(...used));
    try {
      await addDoc(collection(db, 'taskPeople'), { name: trimmed, color, createdAt: new Date().toISOString() });
      setName('');
    } catch (err) {
      console.error('Kişi eklenemedi:', err);
      alert('Kişi eklenemedi.');
    }
  };

  const handleRename = async () => {
    const trimmed = (editing?.name || '').trim();
    if (!trimmed) return;
    try {
      await updateDoc(doc(db, 'taskPeople', editing.id), { name: trimmed });
      setEditing(null);
    } catch (err) {
      console.error('Kişi güncellenemedi:', err);
      alert('Kişi güncellenemedi.');
    }
  };

  const handleRemove = async (id) => {
    try {
      await deleteDoc(doc(db, 'taskPeople', id));
      setConfirmId(null);
    } catch (err) {
      console.error('Kişi silinemedi:', err);
      alert('Kişi silinemedi.');
    }
  };

  return (
    <div className="todo-modal-overlay" onClick={onClose}>
      <div className="todo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="todo-modal-head">
          <h3>Kişiler</h3>
          <button type="button" className="todo-icon-btn" onClick={onClose} aria-label="Kapat"><i className="fas fa-times"></i></button>
        </div>
        <form className="todo-people-add" onSubmit={handleAdd}>
          <input type="text" placeholder="Ad Soyad" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <button type="submit" className="todo-btn-primary" disabled={!name.trim()}><i className="fas fa-plus"></i> Ekle</button>
        </form>

        {people.length === 0 ? (
          <p className="todo-modal-text">Henüz kişi yok. Görev atayacağınız kişileri ekleyin.</p>
        ) : (
          <ul className="todo-people">
            {people.map((person) => (
              <li key={person.id}>
                <PersonAvatar person={person} name={person.name} />
                {editing?.id === person.id ? (
                  <input
                    className="todo-people-edit"
                    value={editing.name}
                    autoFocus
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(null); }}
                  />
                ) : (
                  <span className="todo-people-name">
                    {person.name}
                    <small>{openCount(person.id)} açık görev</small>
                  </span>
                )}
                {confirmId === person.id ? (
                  <span className="todo-people-confirm">
                    Silinsin mi?
                    <button type="button" className="todo-btn-danger is-sm" onClick={() => handleRemove(person.id)}>Sil</button>
                    <button type="button" className="todo-btn-ghost is-sm" onClick={() => setConfirmId(null)}>Vazgeç</button>
                  </span>
                ) : editing?.id === person.id ? (
                  <span className="todo-people-actions">
                    <button type="button" className="todo-icon-btn" onClick={handleRename} aria-label="Kaydet"><i className="fas fa-check"></i></button>
                    <button type="button" className="todo-icon-btn" onClick={() => setEditing(null)} aria-label="Vazgeç"><i className="fas fa-times"></i></button>
                  </span>
                ) : (
                  <span className="todo-people-actions">
                    <button type="button" className="todo-icon-btn" onClick={() => setEditing({ id: person.id, name: person.name })} aria-label="Adı düzenle"><i className="fas fa-pen"></i></button>
                    <button type="button" className="todo-icon-btn is-danger" onClick={() => setConfirmId(person.id)} aria-label="Sil"><i className="fas fa-trash-alt"></i></button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="todo-modal-info">Silinen kişinin görevleri silinmez; görevlerde adı görünmeye devam eder.</p>
      </div>
    </div>
  );
};

export default Todos;
